type ProvisioningAttribution = {
	touchpointName?: string;
	upgradeSourceProduct?: 'jira' | 'confluence';
};

export type ProvisionPlanRequest = ProvisioningAttribution & {
	cloudId: string;
	requestedEditionId?: Edition;
	sourceComponent: string;
	sourceContext: string;
};

type Edition = 'free' | 'standard' | 'premium';
export type ProvisioningJobName = 'core' | 'core-with-rovo';

type Task = {
	context?: {
		edition: {
			id: Edition;
		};
		hasPaymentDetails: boolean;
	};
	name: string;
};

export type ProvisionStatus =
	| 'PROVISIONING_COMPLETED'
	| 'PROVISIONING_FAILED'
	| 'PROVISIONING_FAILED_COMPLETED'
	| 'PROVISIONING_IN_PROGRESS'
	| 'PROVISIONING_ABORTED'
	| 'PROVISIONING_PROCESS_NOT_FOUND';

export type ProvisionPlanResponseAllowed = {
	allowed: true;
	tasks: Task[];
};

export type ProvisionPlanResponseNotAllowed =
	| {
			allowed: false;
			context?: {
				status: ProvisionStatus;
			};
			reasons: string[];
			tasks: [];
	  }
	| {
			allowed: false;
			reasons: string[];
			tasks: Task[];
	  };

export type ProvisionPlanResponse = ProvisionPlanResponseAllowed | ProvisionPlanResponseNotAllowed;

export type ProvisionStartRequest = ProvisioningAttribution & {
	cloudId: string;
	jobName: ProvisioningJobName;
	locale: string;
	requestedEditionId?: Edition;
	signal?: AbortSignal;
	sourceComponent: string;
	sourceContext: string;
};

export type ProvisionStartNormalizeSeatRequest = {
	cloudId: string;
	signal?: AbortSignal;
};

export type ProvisionStartResponse = {
	allowed: boolean;
	started: boolean;
	tasks: Task[];
	reasons?: string[];
};

export type ProvisionStatusRequest = {
	cloudId: string;
	jobName: ProvisioningJobName;
};

export type ProvisionStatusResponse = {
	context?: {
		onboardingUrl?: string;
		progressUri: string;
	};
	status: ProvisionStatus;
	step: string;
};

export type PollingOptions = {
	initialDelay: number;
	interval: number;
	maxDuration: number;
	signal: AbortSignal;
};

export type ProvisionStartAndTrackResquest = ProvisioningAttribution & {
	callback: (progress: number, status: ProvisionStatusResponse) => void;
	cloudId: string;
	initialStatus?: ProvisionStatus;
	locale: string;
	pollingOptions: PollingOptions;
	requestedEditionId?: Edition;
	sourceComponent: string;
	sourceContext: string;
};

export type TrackProvisionJobRequest = {
	callback: (progress: number, status: ProvisionStatusResponse) => void;
	cloudId: string;
	initialStatus: ProvisionStatusResponse;
	jobName: ProvisioningJobName;
	pollingOptions: PollingOptions;
	startRequest: ProvisionStartRequest;
	tasks: Task[];
};

export type TrackProvisionJobResponse = ProvisionStatusResponse & {
	progress: number;
};

export type PollingState = {
	attempts: number;
	startTime: number;
};

export class PollingTimeoutError extends Error {
	public readonly maxDuration: number;

	constructor(maxDuration: number) {
		super(`Polling timed out after ${maxDuration}ms`);
		this.name = 'PollingTimeoutError';
		this.maxDuration = maxDuration;
	}

	static is(error: unknown): error is PollingTimeoutError {
		return (
			error instanceof PollingTimeoutError ||
			(error instanceof Error && error.name === 'PollingTimeoutError' && 'maxDuration' in error)
		);
	}
}
